import { Box } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AutocompleteDropdown } from '~/components/AutocompleteDropdown';
import FlagIcon from '~/components/FlagIcon';
import { countries } from '~/constants';
import { getValidFilterTypes } from '~/screens/WhalarModeScreen/components/utils';
import {
  GroupedFilters,
  useGroupFilters,
} from '~/screens/WhalarModeSharedListScreen/hooks/useGroupFilters';
import { useAdvancedFiltersStore } from '~/screens/WhalarModeSharedListScreen/store/useAdvancedFilterStore';

import { PLATFORM_OPTIONS } from '../PlatformsToggle';
import { FILTERS } from '../SearchByFollowers';

export const LocationInput: React.FC = () => {
  const { activePlatform, setFilter, getFilter, appliedFilters, clearFilters } =
    useAdvancedFiltersStore();

  const appliedFiltersGrouped = useGroupFilters(appliedFilters);

  const selectedLocation = getFilter(activePlatform, FILTERS.country) || '';

  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState({
    [PLATFORM_OPTIONS.selectPlatform]: '',
    [PLATFORM_OPTIONS.instagram]: '',
    [PLATFORM_OPTIONS.tiktok]: '',
    [PLATFORM_OPTIONS.youtube]: '',
    [PLATFORM_OPTIONS.snapchat]: '',
  });

  const filteredCountries = useMemo(() => {
    if (activePlatform === 'snapchat') {
      return [];
    }
    const list = countries.filter((country) =>
      country.name.toLowerCase().includes(value[activePlatform].toLowerCase()),
    );

    return list.map(({ code: label, name: value }) => ({
      label,
      value,
      icon: (
        <Box borderRadius={1}>
          <FlagIcon code={label} boxSize={4} />
        </Box>
      ),
    }));
  }, [value, activePlatform]);

  const country = useMemo(() => {
    return countries.find(({ code }) => code === selectedLocation);
  }, [selectedLocation]);

  useEffect(() => {
    if (
      activePlatform !== 'snapchat' &&
      country?.name &&
      country?.name !== value[activePlatform]
    ) {
      setValue({ ...value, [activePlatform]: country?.name });
    } else if (
      activePlatform !== 'snapchat' &&
      value[activePlatform] &&
      country?.name !== value[activePlatform]
    ) {
      setValue({ ...value, [activePlatform]: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, activePlatform, setValue]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleSelectLocation = useCallback(
    ({ label, value: name }: { label: string; value: string }) => {
      setFilter(activePlatform, FILTERS.country, label);
      setValue({ ...value, [activePlatform]: name });
      handleClose();
    },
    [setFilter, setValue, handleClose, activePlatform, value],
  );

  const handleRemoveSelectedLocation = useCallback(() => {
    setFilter(activePlatform, FILTERS.country, '');
    setValue({ ...value, [activePlatform]: '' });
    const selectedFilter = appliedFiltersGrouped.find(
      (filter) =>
        filter.platform === activePlatform && selectedLocation === filter.value,
    ) as GroupedFilters;
    clearFilters(activePlatform, getValidFilterTypes(selectedFilter));
  }, [
    setFilter,
    activePlatform,
    appliedFiltersGrouped,
    selectedLocation,
    clearFilters,
    value,
  ]);

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      const inputValue = e.target.value;

      if (inputValue) {
        setValue({ ...value, [activePlatform]: inputValue });
      } else {
        setValue({ ...value, [activePlatform]: '' });
      }
    },
    [setValue, activePlatform, value],
  );

  return (
    <AutocompleteDropdown
      isOpen={isOpen}
      value={value[activePlatform]}
      selectedValue={country?.name as string}
      options={filteredCountries}
      placeholder="Select Location"
      onChange={handleSearch}
      onSelect={handleSelectLocation}
      onRemove={handleRemoveSelectedLocation}
      onMenuOpen={handleOpen}
      onMenuClose={handleClose}
      isDisabled={
        activePlatform === PLATFORM_OPTIONS.snapchat ||
        (activePlatform as string) === PLATFORM_OPTIONS.selectPlatform
      }
      marginTop={4}
    />
  );
};
